import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Sparkles, Lock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** A 13-17 young person claiming the FuturePath a parent started. Their password, their profile. */
export default function ClaimProfilePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [f, setF] = useState({ password: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/family/activation/${token}`)
      .then(r => setInfo(r.data))
      .catch(e => setError(e?.response?.data?.detail || 'That link is no longer valid'));
  }, [token]);

  const submit = async () => {
    if (f.password.length < 8) return toast.error('Use at least 8 characters');
    if (f.password !== f.confirm) return toast.error("Those passwords don't match");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/family/activation/${token}`, { password: f.password });
      localStorage.setItem('futurepath_token', r.data.access_token);
      window.location.assign(r.data.user.onboarding_complete ? '/app/dashboard' : '/app/welcome');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not set that password');
    } finally { setSaving(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-navy-gradient flex items-center justify-center p-6">
        <div className="max-w-md text-center" data-testid="claim-error">
          <Lock className="w-8 h-8 text-rose-300 mx-auto" />
          <p className="text-white mt-4">{error}</p>
          <p className="text-sm text-slate-300 mt-2">
            Ask the parent who set this up to send it again, or to give you the password they chose.
          </p>
        </div>
      </div>
    );
  }
  if (!info) return <div className="min-h-screen bg-navy-gradient" />;

  return (
    <div className="min-h-screen bg-navy-gradient flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg rounded-3xl border border-white/10 bg-slate-900/70 backdrop-blur-xl p-7 md:p-9"
        data-testid="claim-profile-page">
        <Sparkles className="w-6 h-6 text-cyan-300" />
        <h1 className="font-outfit text-3xl font-semibold text-white mt-5">
          This one's yours, {info.first_name}.
        </h1>
        <p className="text-sm text-slate-300 mt-3 leading-relaxed">
          {info.parent_name || 'A parent'} started your FuturePath. Choose a password and it becomes
          yours, your answers, your plan, your pace. They can see your plan and progress; what you
          write in your private notes stays yours.
        </p>
        <p className="text-xs text-slate-300 mt-3">You'll sign in with {info.email}</p>

        <div className="space-y-4 mt-6">
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Choose a password</Label>
            <Input type="password" value={f.password} onChange={e => setF({ ...f, password: e.target.value })}
              className="input-dark" data-testid="claim-password" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Type it again</Label>
            <Input type="password" value={f.confirm} onChange={e => setF({ ...f, confirm: e.target.value })}
              className="input-dark" data-testid="claim-confirm" />
          </div>
        </div>

        <Button onClick={submit} disabled={saving} data-testid="claim-submit"
          className="w-full rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold mt-6">
          {saving ? 'Setting up…' : <>Make it mine <ArrowRight className="w-4 h-4 ml-1.5" /></>}
        </Button>
      </motion.div>
    </div>
  );
}
